import { ZuruflexApiError } from '@zuruflex/api-client';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import type { Vehicle } from '@zuruflex/types';
import { Alert, Button, DateRangePicker, FormField, Input } from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

/** Whole days: a block that starts at noon leaves half a bookable morning. */
function dayStart(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function dayEnd(date: Date): Date {
  const end = new Date(date);
  end.setHours(23, 59, 59, 0);
  return end;
}

/**
 * The owner's own calendar for one car.
 *
 * Dates are taken as whole days, because "I need it that weekend" is how
 * people think about their own car — the times are pinned to the ends of
 * the day so a block never leaves half a bookable morning behind.
 */
export function BlackoutEditor({ vehicle }: { vehicle: Vehicle }) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();

  /**
   * One range rather than two dates. "I need it that weekend" is a span,
   * and the pair of boxes let a partner set a To before the From and only
   * find out when the API turned it down.
   */
  const [dates, setDates] = useState<DateRange | undefined>();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['blackouts', vehicle.id],
    queryFn: () => api.listBlackouts(vehicle.id),
  });

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: ['blackouts', vehicle.id] });
  }

  const add = useMutation({
    mutationFn: () => {
      // A one-day block comes back with no `to`, and means that day.
      const start = dates?.from;
      const end = dates?.to ?? dates?.from;
      if (!start || !end) throw new Error('no dates');
      return api.addBlackout(vehicle.id, {
        startAt: dayStart(start).toISOString(),
        endAt: dayEnd(end).toISOString(),
        reason: reason.trim() || undefined,
      });
    },
    onMutate: () => setError(null),
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
    onSuccess: async () => {
      setDates(undefined);
      setReason('');
      await invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.removeBlackout(vehicle.id, id),
    onSuccess: invalidate,
  });

  return (
    <div>
      <p className="text-sm text-text-muted">{t('calendar.subtitle')}</p>

      {error ? (
        <Alert variant="danger" className="mt-4">
          {error}
        </Alert>
      ) : null}

      <form
        className="mt-5 flex flex-col gap-3"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <FormField label={t('calendar.dates')} required>
          {(field) => (
            <DateRangePicker
              id={field.id}
              value={dates}
              placeholder={t('calendar.datesPlaceholder')}
              // One month: this panel is a narrow column over the fleet,
              // and two would not fit beside it.
              numberOfMonths={1}
              onChange={setDates}
            />
          )}
        </FormField>

        <FormField label={t('calendar.reason')}>
          {(field) => (
            <Input {...field} value={reason} onChange={(event) => setReason(event.target.value)} />
          )}
        </FormField>

        <Button type="submit" disabled={add.isPending || !dates?.from}>
          {add.isPending ? t('common:state.working') : t('calendar.block')}
        </Button>
      </form>

      <ul className="mt-6 flex flex-col gap-2">
        {!isPending && (data?.length ?? 0) === 0 ? (
          <li className="text-sm text-text-muted">{t('calendar.none')}</li>
        ) : null}

        {data?.map((blackout) => (
          <li
            key={blackout.id}
            className="flex items-center justify-between gap-3 rounded-field border border-border-default px-3 py-2"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {formatDateTimeIn(blackout.startAt, locale)} →{' '}
                {formatDateTimeIn(blackout.endAt, locale)}
              </p>
              {blackout.reason ? (
                <p className="truncate text-xs text-text-muted">{blackout.reason}</p>
              ) : null}
            </div>
            <Button
              size="sm"
              variant="ghost"
              disabled={remove.isPending}
              onClick={() => remove.mutate(blackout.id)}
            >
              {t('calendar.remove')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
